import { useState } from 'react';
import { Lightbulb, BookOpen, MessageSquare, Zap, HeadphonesIcon, Brain, Download, Smartphone } from 'lucide-react';
import WaveAnimation from '../components/WaveAnimation';
import AppDownloadPopup from '../components/common/AppDownloadPopup';

const steps = [
  {
    icon: Smartphone,
    title: 'Download Our App',
    description: 'Get started by downloading our app, available on iOS and Android, for seamless learning on the go.',
    details: 'Access your courses anytime, anywhere with our mobile app. Enjoy offline learning, push notifications for updates, and a seamless cross-device experience.',
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80',
    hasDownload: true
  },
  {
    icon: BookOpen,
    title: 'Choose Your Course',
    description: 'Explore our range of expertly designed courses and pick the one that matches your goals.',
    details: 'Browse through our comprehensive course catalog, each carefully crafted by industry experts and enhanced with AI-powered learning tools.',
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80'
  },
  {
    icon: Brain,
    title: 'AI-Powered Assistance',
    description: 'Each course comes with a dedicated AI assistant trained on the course material, ready to help.',
    details: 'Our advanced AI assistants are specifically trained on your chosen course content, providing expert-level guidance and support throughout your learning journey.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80'
  },
  {
    icon: MessageSquare,
    title: 'Ask Questions Anytime',
    description: 'Use chat or voice to ask anything about the course. Our AI provides instant, accurate answers tailored to your learning needs.',
    details: 'Get immediate, contextual responses to your questions through text or voice interaction, making learning as natural as having a conversation.',
    image: 'https://images.unsplash.com/photo-1676186185408-9ed2d6df6753?auto=format&fit=crop&q=80'
  },
  {
    icon: Zap,
    title: 'Interactive Learning',
    description: 'Experience a dynamic and engaging learning journey with personalized guidance at every step.',
    details: 'Engage with interactive exercises, real-time feedback, and adaptive learning paths that adjust to your progress and learning style.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80'
  },
  {
    icon: HeadphonesIcon,
    title: 'Continuous Support',
    description: "Whether you're stuck on a topic or need deeper insights, our AI assistant ensures you never stop learning.",
    details: 'Access 24/7 support from our AI system, ensuring you can overcome challenges and maintain momentum in your learning journey.',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80'
  }
];

export default function HowItWorksPage() {
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-900 pt-24 relative overflow-hidden">
      <WaveAnimation />
      
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            How It Works
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Experience a revolutionary learning journey powered by AI technology
            that adapts to your unique needs.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div key={index} className="relative mb-12 last:mb-0">
              <div 
                className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-8 hover:bg-gray-800/60 transition-colors cursor-pointer"
                onClick={() => {
                  if (step.hasDownload) {
                    setShowDownloadPopup(true);
                  }
                  setSelectedStep(index);
                }}
              >
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-purple-600/20 rounded-lg flex items-center justify-center">
                      <step.icon className="w-8 h-8 text-purple-400" />
                    </div>
                  </div>
                  
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold text-white mb-4">
                      {step.title}
                      {step.hasDownload && (
                        <button 
                          className="ml-4 inline-flex items-center text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-1 rounded-full transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDownloadPopup(true);
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </button>
                      )}
                    </h3>
                    <p className="text-xl text-purple-200 mb-4">
                      {step.description}
                    </p>
                    <p className="text-gray-300 leading-relaxed">
                      {step.details}
                    </p>
                  </div>
                  
                  <div className="flex-shrink-0 w-full md:w-64">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-48 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </div>
              
              {index < steps.length - 1 && (
                <div className="hidden md:flex justify-center my-8">
                  <div className="w-0.5 h-8 bg-purple-600/20"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <AppDownloadPopup 
        isOpen={showDownloadPopup}
        onClose={() => setShowDownloadPopup(false)}
      />
    </div>
  );
}